import { Alert, Box, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material'
import { FC, FormEvent, useEffect, useState } from 'react'

import { getToken, setDeviceToken } from '../auth/session'
import { authService } from '../services/auth-service'
import useBrand from '../tenant/useBrand'

/**
 * Standalone, full-screen "set up your authenticator" page shown right after a
 * first-time login (the backend reports `hasSeenTwoFactorSetup === false`). It
 * lives outside the app shell — the login gate (AuthGate) routes `/setup-2fa`
 * here while authenticated. The Security settings page remains the place to
 * manage 2FA later; this is only the one-time onboarding prompt.
 */
const Setup2FAPage: FC = () => {
  const brand = useBrand()
  const [qrCodeUrl, setQrCodeUrl] = useState('')
  const [code, setCode] = useState('')
  const [trustDevice, setTrustDevice] = useState(false)
  const [generating, setGenerating] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const token = getToken()
    if (!token) {
      window.location.assign('/')
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const res = await authService.generate2FA(token)
        if (!cancelled) setQrCodeUrl(res.qrCodeUrl)
      } catch {
        if (!cancelled) setError('Failed to generate a 2FA secret. Please try again.')
      } finally {
        if (!cancelled) setGenerating(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const onEnable = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    if (code.length !== 6) {
      setError('Please enter the 6-digit code from your authenticator app.')
      return
    }
    const token = getToken()
    if (!token) {
      window.location.assign('/')
      return
    }
    setSubmitting(true)
    try {
      const res = await authService.enable2FA(code, token, trustDevice)
      // Persist the trusted-device token so this device can skip the next 2FA
      // challenge for 30 days.
      if (res?.deviceToken) setDeviceToken(res.deviceToken)
      window.location.assign('/app')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to verify code')
      setSubmitting(false)
    }
  }

  // Record that the prompt was shown so the user isn't asked again, then enter
  // the app.
  const onSkip = async () => {
    setSubmitting(true)
    const token = getToken()
    try {
      if (token) await authService.skip2FA(token)
    } catch {
      /* even if the skip call fails, let them into the app */
    } finally {
      window.location.assign('/app')
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#ededed',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 420 }}>
        <Stack alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
          {brand.logoUrl && (
            <Box
              alt={brand.name}
              component="img"
              src={brand.logoUrl}
              sx={{ height: 48, maxWidth: 200, objectFit: 'contain' }}
            />
          )}
        </Stack>

        <Box
          sx={{
            bgcolor: 'common.white',
            borderRadius: 3,
            p: 4,
            boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
          }}
        >
          <Stack spacing={2.5}>
            <Box>
              <Typography component="h1" variant="h4" sx={{ fontWeight: 600 }}>
                Set Up Authenticator
              </Typography>
              <Typography sx={{ color: 'grey.600', mt: 1 }} variant="body2">
                Scan the QR code below with an authenticator app (such as Google Authenticator,
                Microsoft Authenticator, or Authy), then enter the 6-digit code it generates.
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" variant="standard">
                {error}
              </Alert>
            )}

            {generating ? (
              <Stack alignItems="center" sx={{ py: 6 }}>
                <CircularProgress />
              </Stack>
            ) : (
              <>
                {qrCodeUrl && (
                  <Stack alignItems="center">
                    <Box
                      alt="2FA QR code"
                      component="img"
                      src={qrCodeUrl}
                      sx={{ width: 192, height: 192, border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}
                    />
                  </Stack>
                )}

                <Box component="form" onSubmit={onEnable}>
                  <Stack spacing={2.5}>
                    <TextField
                      autoFocus
                      fullWidth
                      inputProps={{
                        maxLength: 6,
                        inputMode: 'numeric',
                        style: { textAlign: 'center', letterSpacing: '0.4em' },
                      }}
                      label="Enter 6-digit code"
                      placeholder="000000"
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                    />

                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                      <input
                        checked={trustDevice}
                        type="checkbox"
                        onChange={(e) => setTrustDevice(e.target.checked)}
                      />
                      <Typography sx={{ color: 'grey.700' }} variant="body2">
                        Trust this device for 30 days
                      </Typography>
                    </label>

                    <Button
                      color="primary"
                      disabled={submitting || code.length !== 6}
                      size="large"
                      type="submit"
                      variant="contained"
                      sx={{ borderRadius: 9999, py: 1.2 }}
                    >
                      {submitting ? 'Verifying…' : 'Verify & Enable'}
                    </Button>

                    <Button
                      color="primary"
                      disabled={submitting}
                      size="large"
                      variant="outlined"
                      sx={{ borderRadius: 9999, py: 1.2 }}
                      onClick={onSkip}
                    >
                      Skip for now
                    </Button>
                  </Stack>
                </Box>
              </>
            )}
          </Stack>
        </Box>
      </Box>
    </Box>
  )
}

export default Setup2FAPage
